/**
 * Route guard — redirects to /login if the user isn't authenticated, and to
 * a role-appropriate landing if they are authenticated but lack the requested
 * role. Renders a centered spinner during the initial auth bootstrap.
 */
import type { UserRole } from '@peerless/shared';
import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '../../hooks/useAuth';


interface RequireAuthProps {
  children: ReactNode;
  /** If set, also requires the user have one of these roles. */
  roles?: UserRole[];
}

export function RequireAuth({ children, roles }: RequireAuthProps): JSX.Element {
  const { user, status } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div className="grid min-h-full place-items-center">
        <div className="inline-flex items-center gap-2 text-sm text-ink-muted">
          <span className="size-2 animate-pulse rounded-tight bg-muted" />
          loading…
        </div>
      </div>
    );
  }

  if (status === 'unauthenticated' || !user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (roles && !roles.includes(user.role)) {
    const fallback = user.role === 'admin' ? '/admin/users' : '/chat';
    return <Navigate to={fallback} replace />;
  }

  return <>{children}</>;
}
